import { useState } from 'react';
import { Play, TrendingUp, Users, Instagram, Youtube, Music2, X, Plus, ChevronDown, Search } from 'lucide-react';
import { ImageWithFallback } from './figma/ImageWithFallback';

interface Creator {
  id: string;
  name: string;
  handle: string;
  avatar: string;
  coverImage?: string;
  category: string;
  followers: string;
  engagementRate: string;
  platform: 'Instagram' | 'YouTube' | 'TikTok' | 'Snapchat' | 'Multi';
  verified?: boolean;
}

interface FilterState {
  country: string;
  language: string;
  gender: string;
  minAge: string;
  maxAge: string;
  audienceLanguage: string;
  audienceGender: string;
  audienceMinAge: string;
  audienceMaxAge: string;
  creatorInterests: string;
  topics: string;
  hashtags: string;
  minFollowers: string;
  maxFollowers: string;
  minEngagement: string;
  hasEmail: boolean;
  sortBy: string;
}

const mockCreators: Creator[] = [
  {
    id: '1',
    name: 'Pedro Guilherme',
    handle: '@pedroguilherme',
    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400',
    coverImage: 'https://images.unsplash.com/photo-1522778119026-d647f0596c20?w=1200',
    category: 'Sports & Lifestyle',
    followers: '7.3M',
    engagementRate: '5.3%',
    platform: 'Multi',
    verified: true
  },
  {
    id: '2',
    name: 'Sarah Johnson',
    handle: '@sarahjohnson',
    avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400',
    category: 'Fashion & Beauty',
    followers: '2.1M',
    engagementRate: '7.8%',
    platform: 'Instagram',
    verified: true
  },
  {
    id: '3',
    name: 'Marcus Chen',
    handle: '@marcuschen',
    avatar: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=400',
    category: 'Tech Reviews',
    followers: '1.8M',
    engagementRate: '6.2%',
    platform: 'YouTube',
    verified: true
  },
  {
    id: '4',
    name: 'Emma Rodriguez',
    handle: '@emmarodriguez',
    avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400',
    category: 'Travel & Adventure',
    followers: '3.4M',
    engagementRate: '4.9%',
    platform: 'Instagram',
    verified: true
  },
  {
    id: '5',
    name: 'Alex Turner',
    handle: '@alexturner',
    avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400',
    category: 'Fitness & Health',
    followers: '1.2M',
    engagementRate: '8.1%',
    platform: 'TikTok',
    verified: false
  },
  {
    id: '6',
    name: 'Olivia Park',
    handle: '@oliviapark',
    avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=400',
    category: 'Food & Cooking',
    followers: '950K',
    engagementRate: '9.3%',
    platform: 'Instagram',
    verified: true
  },
  {
    id: '7',
    name: 'David Kim',
    handle: '@davidkim',
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400',
    category: 'Gaming',
    followers: '5.6M',
    engagementRate: '3.7%',
    platform: 'YouTube',
    verified: true
  },
  {
    id: '8',
    name: 'Lisa Martinez',
    handle: '@lisamartinez',
    avatar: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=400',
    category: 'Lifestyle',
    followers: '2.8M',
    engagementRate: '5.6%',
    platform: 'Multi',
    verified: true
  },
  {
    id: '9',
    name: 'James Wilson',
    handle: '@jameswilson',
    avatar: 'https://images.unsplash.com/photo-1519345182560-3f2917c472ef?w=400',
    category: 'Comedy',
    followers: '4.2M',
    engagementRate: '6.8%',
    platform: 'TikTok',
    verified: true
  },
  {
    id: '10',
    name: 'Sophia Lee',
    handle: '@sophialee',
    avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=400',
    category: 'Fashion',
    followers: '1.5M',
    engagementRate: '7.2%',
    platform: 'Instagram',
    verified: false
  }
];

const topPerformers = mockCreators.slice(0, 5);
const trending = mockCreators.slice(5, 10);
const recommended = [...mockCreators].sort(() => 0.5 - Math.random()).slice(0, 5);

export function CreatorsListPage() {
  const [hoveredCreator, setHoveredCreator] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'creators' | 'creatorlists'>('creators');
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>(['Instagram']);
  const [showPlatformMenu, setShowPlatformMenu] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<FilterState>({
    country: '',
    language: '',
    gender: 'Any',
    minAge: '18',
    maxAge: '100',
    audienceLanguage: '',
    audienceGender: 'Any',
    audienceMinAge: '18',
    audienceMaxAge: '100',
    creatorInterests: '',
    topics: '',
    hashtags: '',
    minFollowers: 'Min Followers',
    maxFollowers: 'Max Followers',
    minEngagement: '2.5',
    hasEmail: false,
    sortBy: 'Default'
  });

  const availablePlatforms = ['Instagram', 'TikTok', 'YouTube', 'Snapchat'];

  const togglePlatform = (platform: string) => {
    setSelectedPlatforms(prev => 
      prev.includes(platform) 
        ? prev.filter(p => p !== platform)
        : [...prev, platform]
    );
  };

  const removePlatform = (platform: string) => {
    setSelectedPlatforms(prev => prev.filter(p => p !== platform));
  };

  const handleClearFilters = () => {
    setFilters({
      country: '',
      language: '',
      gender: 'Any',
      minAge: '18',
      maxAge: '100',
      audienceLanguage: '',
      audienceGender: 'Any',
      audienceMinAge: '18',
      audienceMaxAge: '100',
      creatorInterests: '',
      topics: '',
      hashtags: '',
      minFollowers: 'Min Followers',
      maxFollowers: 'Max Followers',
      minEngagement: '2.5',
      hasEmail: false,
      sortBy: 'Default'
    });
  };

  const handleApplyFilters = () => {
    setShowFilters(false);
    // Apply filter logic here
  };

  const getPlatformIcon = (platform: Creator['platform']) => {
    switch (platform) {
      case 'Instagram':
        return <Instagram className="w-4 h-4" />;
      case 'YouTube':
        return <Youtube className="w-4 h-4" />;
      case 'TikTok':
        return <Music2 className="w-4 h-4" />;
      case 'Multi':
        return <Users className="w-4 h-4" />;
      default:
        return null;
    }
  };

  const CreatorCard = ({ creator }: { creator: Creator }) => (
    <div
      className="group bg-[#181818] p-4 rounded-lg hover:bg-[#282828] transition-all duration-300 cursor-pointer"
      onMouseEnter={() => setHoveredCreator(creator.id)}
      onMouseLeave={() => setHoveredCreator(null)}
    >
      <div className="relative mb-4">
        <div className="aspect-square rounded-md overflow-hidden bg-[#282828] shadow-lg">
          <ImageWithFallback
            src={creator.avatar}
            alt={creator.name}
            className="w-full h-full object-cover"
          />
        </div>
        {hoveredCreator === creator.id && (
          <div className="absolute bottom-2 right-2 w-12 h-12 bg-green-500 rounded-full flex items-center justify-center shadow-xl opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
            <Play className="w-5 h-5 text-black fill-black ml-0.5" />
          </div>
        )}
      </div>
      
      <div className="mb-2">
        <h3 className="text-white truncate group-hover:underline mb-1">
          {creator.name}
        </h3>
        <p className="text-sm text-gray-400 truncate mb-2">{creator.category}</p>
        
        <div className="flex items-center gap-3 text-sm">
          <div className="flex items-center gap-1.5 text-gray-400">
            <Users className="w-4 h-4" />
            <span>{creator.followers}</span>
          </div>
          <div className="flex items-center gap-1.5 text-gray-400">
            <TrendingUp className="w-4 h-4" />
            <span>{creator.engagementRate}</span>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <div className="text-gray-400">{getPlatformIcon(creator.platform)}</div>
        {creator.verified && (
          <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
            <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="bg-gradient-to-b from-[#1e3a5f] to-black pb-6">
        <div className="px-8 pt-16 pb-6">
          <h1 className="text-8xl mb-4">Creators</h1>
          <p className="text-xl text-gray-300">Discover top creators for your campaigns</p>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="px-8 py-6 border-b border-gray-800">
        <div className="flex items-center gap-4 mb-4">
          {/* Creators/Creatorlists Toggle */}
          <div className="inline-flex rounded-full bg-gray-900 p-1">
            <button
              onClick={() => setViewMode('creators')}
              className={`px-6 py-2 rounded-full text-sm transition-colors ${
                viewMode === 'creators'
                  ? 'bg-white text-black'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Creators
            </button>
            <button
              onClick={() => setViewMode('creatorlists')}
              className={`px-6 py-2 rounded-full text-sm transition-colors ${
                viewMode === 'creatorlists'
                  ? 'bg-white text-black'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Creatorlists
            </button>
          </div>
        </div>

        <div className="flex items-center gap-4">
          {/* Platform Chips */}
          <div className="flex items-center gap-2">
            {selectedPlatforms.map(platform => (
              <div
                key={platform}
                className="flex items-center gap-2 px-4 py-2 bg-white text-black rounded-full text-sm"
              >
                <span>{platform}</span>
                <button
                  onClick={() => removePlatform(platform)}
                  className="hover:opacity-70 transition-opacity"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
            
            {/* Add Platform Button */}
            <div className="relative">
              <button
                onClick={() => setShowPlatformMenu(!showPlatformMenu)}
                className="w-10 h-10 rounded-full border-2 border-gray-600 flex items-center justify-center hover:border-white transition-colors"
              >
                <Plus className="w-5 h-5" />
              </button>
              
              {showPlatformMenu && (
                <div className="absolute top-full left-0 mt-2 bg-white text-black rounded-lg shadow-xl p-2 z-50 min-w-[160px]">
                  {availablePlatforms.map(platform => (
                    <label
                      key={platform}
                      className="flex items-center gap-3 px-3 py-2 hover:bg-gray-100 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedPlatforms.includes(platform)}
                        onChange={() => togglePlatform(platform)}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">{platform}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Search Bar */}
          <div className="flex-1 flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search anything - creatorlists, influencers, topics..."
                className="w-full pl-12 pr-4 py-3 bg-transparent border border-gray-600 rounded-full text-white placeholder-gray-400 focus:outline-none focus:border-white transition-colors"
              />
            </div>
            <button className="px-6 py-3 text-gray-400 hover:text-white transition-colors">
              Search
            </button>
          </div>

          {/* Filters Button */}
          <div className="relative">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="px-6 py-3 border border-gray-600 rounded-full hover:border-white transition-colors flex items-center gap-2"
            >
              <span>Filters</span>
              <ChevronDown className="w-4 h-4" />
            </button>

            {/* Filter Dropdown */}
            {showFilters && (
              <div className="absolute top-full right-0 mt-2 w-[720px] bg-white text-black rounded-lg shadow-2xl z-50 max-h-[600px] overflow-y-auto">
                <div className="p-6">
                  <h3 className="text-lg mb-4">Filter Options</h3>
                  
                  <div className="grid grid-cols-2 gap-6">
                    {/* Creator Demographics */}
                    <div>
                      <h4 className="text-sm mb-3">Creator Demographics</h4>
                      
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Country</label>
                          <select
                            value={filters.country}
                            onChange={(e) => setFilters({...filters, country: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                          >
                            <option value="">Select country...</option>
                            <option value="us">United States</option>
                            <option value="uk">United Kingdom</option>
                            <option value="ca">Canada</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Language</label>
                          <select
                            value={filters.language}
                            onChange={(e) => setFilters({...filters, language: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                          >
                            <option value="">Select language...</option>
                            <option value="en">English</option>
                            <option value="es">Spanish</option>
                            <option value="fr">French</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Gender</label>
                          <select
                            value={filters.gender}
                            onChange={(e) => setFilters({...filters, gender: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                          >
                            <option value="Any">Any</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                            <option value="Non-binary">Non-binary</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Age Range</label>
                          <div className="grid grid-cols-2 gap-2">
                            <input
                              type="number"
                              value={filters.minAge}
                              onChange={(e) => setFilters({...filters, minAge: e.target.value})}
                              placeholder="Min Age (18)"
                              className="px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                            />
                            <input
                              type="number"
                              value={filters.maxAge}
                              onChange={(e) => setFilters({...filters, maxAge: e.target.value})}
                              placeholder="Max Age (100)"
                              className="px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Audience Demographics */}
                    <div>
                      <h4 className="text-sm mb-3">Audience Demographics</h4>
                      
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Audience Language</label>
                          <select
                            value={filters.audienceLanguage}
                            onChange={(e) => setFilters({...filters, audienceLanguage: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                          >
                            <option value="">Select language...</option>
                            <option value="en">English</option>
                            <option value="es">Spanish</option>
                            <option value="fr">French</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Audience Gender</label>
                          <div className="grid grid-cols-2 gap-2">
                            <select
                              value={filters.audienceGender}
                              onChange={(e) => setFilters({...filters, audienceGender: e.target.value})}
                              className="px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                            >
                              <option value="Any">Any</option>
                              <option value="Male">Male</option>
                              <option value="Female">Female</option>
                            </select>
                            <input
                              type="text"
                              placeholder="50%"
                              className="px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Audience Age Range</label>
                          <div className="grid grid-cols-2 gap-2">
                            <input
                              type="number"
                              value={filters.audienceMinAge}
                              onChange={(e) => setFilters({...filters, audienceMinAge: e.target.value})}
                              placeholder="Min Age (18)"
                              className="px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                            />
                            <input
                              type="number"
                              value={filters.audienceMaxAge}
                              onChange={(e) => setFilters({...filters, audienceMaxAge: e.target.value})}
                              placeholder="Max Age (100)"
                              className="px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Engagement & Reach */}
                  <div className="mt-6">
                    <h4 className="text-sm mb-3">Engagement & Reach</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Followers Range</label>
                        <div className="grid grid-cols-2 gap-2">
                          <select
                            value={filters.minFollowers}
                            onChange={(e) => setFilters({...filters, minFollowers: e.target.value})}
                            className="px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                          >
                            <option value="Min Followers">Min Followers</option>
                            <option value="1000">1K+</option>
                            <option value="10000">10K+</option>
                            <option value="100000">100K+</option>
                            <option value="1000000">1M+</option>
                          </select>
                          <select
                            value={filters.maxFollowers}
                            onChange={(e) => setFilters({...filters, maxFollowers: e.target.value})}
                            className="px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                          >
                            <option value="Max Followers">Max Followers</option>
                            <option value="10000">10K</option>
                            <option value="100000">100K</option>
                            <option value="1000000">1M</option>
                            <option value="10000000">10M+</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Min Engagement Rate</label>
                        <input
                          type="text"
                          value={filters.minEngagement}
                          onChange={(e) => setFilters({...filters, minEngagement: e.target.value})}
                          placeholder="e.g. 2.5"
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                    </div>

                    <label className="flex items-center gap-2 mt-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filters.hasEmail}
                        onChange={(e) => setFilters({...filters, hasEmail: e.target.checked})}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Has Email Contact</span>
                    </label>
                  </div>

                  {/* Content & Interests */}
                  <div className="mt-6">
                    <h4 className="text-sm mb-3">Content & Interests</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Creator Interests</label>
                        <input
                          type="text"
                          value={filters.creatorInterests}
                          onChange={(e) => setFilters({...filters, creatorInterests: e.target.value})}
                          placeholder="Type and press Enter..."
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                      </div>

                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Topics</label>
                        <input
                          type="text"
                          value={filters.topics}
                          onChange={(e) => setFilters({...filters, topics: e.target.value})}
                          placeholder="Type and press Enter..."
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                      </div>

                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Hashtags</label>
                        <input
                          type="text"
                          value={filters.hashtags}
                          onChange={(e) => setFilters({...filters, hashtags: e.target.value})}
                          placeholder="#example #hashtag"
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Sort By */}
                  <div className="mt-6">
                    <label className="block text-xs text-gray-600 mb-1">Sort By</label>
                    <select
                      value={filters.sortBy}
                      onChange={(e) => setFilters({...filters, sortBy: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="Default">Default</option>
                      <option value="Followers">Followers (High to Low)</option>
                      <option value="Engagement">Engagement Rate (High to Low)</option>
                      <option value="Recent">Recently Added</option>
                    </select>
                  </div>

                  {/* Action Buttons */}
                  <div className="mt-6 flex items-center justify-between gap-4 pt-4 border-t">
                    <button
                      onClick={handleClearFilters}
                      className="px-4 py-2 text-sm text-gray-600 hover:text-black transition-colors"
                    >
                      Clear All
                    </button>
                    <button
                      onClick={handleApplyFilters}
                      className="px-6 py-2 bg-black text-white rounded text-sm hover:bg-gray-800 transition-colors"
                    >
                      Apply Filters
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="px-8 py-8">
        {/* Top Performers Section */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl hover:underline cursor-pointer">Top Performers</h2>
            <button className="text-sm text-gray-400 hover:text-white transition-colors">
              Show all
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {topPerformers.map((creator) => (
              <CreatorCard key={creator.id} creator={creator} />
            ))}
          </div>
        </div>

        {/* Recommended Section */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl hover:underline cursor-pointer">Recommended for You</h2>
            <button className="text-sm text-gray-400 hover:text-white transition-colors">
              Show all
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {recommended.map((creator) => (
              <CreatorCard key={creator.id} creator={creator} />
            ))}
          </div>
        </div>

        {/* Trending Section */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl hover:underline cursor-pointer">Trending Creators</h2>
            <button className="text-sm text-gray-400 hover:text-white transition-colors">
              Show all
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {trending.map((creator) => (
              <CreatorCard key={creator.id} creator={creator} />
            ))}
          </div>
        </div>

        {/* All Creators Section */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl hover:underline cursor-pointer">All Creators</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {mockCreators.map((creator) => (
              <CreatorCard key={creator.id} creator={creator} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}